import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  FairUseConfig,
  FairUseConfigResponse,
  FairUseConfigUpdate,
  FairUseOverviewFilter,
  FairUsePeopleResponse,
  FairUseSummary,
  FairUseTier,
} from "@/api/admin/fair-use"

import { lockBillingMonth } from "@/api/admin/billing"
import {
  approveFairUseRequest,
  createFairUseGrant,
  denyFairUseRequest,
  getFairUseConfig,
  getFairUseOverview,
  getFairUsePeople,
  getFairUseSummary,
  revokeFairUseGrant,
  setFairUseUserTier,
  updateFairUseConfig,
} from "@/api/admin/fair-use"
import { queryKeys } from "@/lib/query-keys"

import { GC_TIME, MEDIUM_STALE_TIME } from "./_shared"

export const FAIR_USE_QUERY_KEY = queryKeys.adminFairUse

/** The overview table (one entry per month) — everything derived from usage. */
export const FAIR_USE_OVERVIEW_QUERY_KEY = [...FAIR_USE_QUERY_KEY, "overview"] as const

/**
 * Users & licences (one entry per month + search term) — the DIRECTORY, with
 * the month left-joined on. A different population from the overview: it lists
 * people who have spent nothing, because a plan has to be assignable to them.
 */
export const FAIR_USE_PEOPLE_QUERY_KEY = [...FAIR_USE_QUERY_KEY, "people"] as const

/** The settings singleton + its value hash. */
export const FAIR_USE_CONFIG_QUERY_KEY = queryKeys.adminFairUseConfig

/**
 * The month header (KPI row, chip counts, lock) — every month at once.
 *
 * A prefix, not `queryKeys.adminFairUseSummary(month)`: mutations invalidate
 * this regardless of which month is currently selected, and fuzzy key
 * matching means it also reaches the per-month key.
 */
export const FAIR_USE_SUMMARY_QUERY_KEY = [...FAIR_USE_QUERY_KEY, "summary"] as const

/**
 * Invalidate both the overview rows and the month summary after a mutation
 * that changes either. Explicit, rather than relying only on the broader
 * `FAIR_USE_QUERY_KEY` prefix invalidation, so the summary's user count can
 * never lag the overview's rows after a top-up, withdrawal, approval, denial
 * or plan change (the footer's "Showing N of M" reads both).
 */
function invalidateFairUseOverviewAndSummary(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: FAIR_USE_OVERVIEW_QUERY_KEY })
  void queryClient.invalidateQueries({ queryKey: FAIR_USE_PEOPLE_QUERY_KEY })
  void queryClient.invalidateQueries({ queryKey: FAIR_USE_SUMMARY_QUERY_KEY })
}

/** Overview page size — bounded pages keep the response and the table cheap. */
export const FAIR_USE_OVERVIEW_PAGE_SIZE = 50

export function useFairUseOverviewInfiniteQuery(
  month?: string,
  enabled = true,
  filter: FairUseOverviewFilter = "all"
) {
  return useInfiniteQuery({
    // The filter is part of the key, not a client-side slice of one cached
    // page: filtering happens server-side, so "pending" and "all" are two
    // different paginated result sets and must not share a continuation.
    queryKey: queryKeys.adminFairUseOverview(month ?? "current", filter),
    queryFn: ({ pageParam }) =>
      getFairUseOverview({
        month,
        limit: FAIR_USE_OVERVIEW_PAGE_SIZE,
        continuation: pageParam ?? undefined,
        filter,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.continuation,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * Users & licences rows: the tenant's people, paged through the directory.
 *
 * The complete-directory table keeps `search` empty and loads cursor pages in
 * the background. Its filters run over that shared cache. The optional server
 * search remains available to other callers under a separate cache key.
 */
export function useFairUsePeopleInfiniteQuery(month?: string, enabled = true, search = "") {
  return useInfiniteQuery({
    queryKey: queryKeys.adminFairUsePeople(month ?? "current", search),
    queryFn: ({ pageParam }) =>
      getFairUsePeople({
        month,
        limit: 200,
        search: search || undefined,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage, pages) =>
      lastPage.cursor && pages.slice(0, -1).some((page) => page.cursor === lastPage.cursor)
        ? undefined
        : lastPage.cursor,
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/** Month header — the KPI row, the chip counts and the billing lock. */
export function useFairUseSummaryQuery(month?: string, enabled = true) {
  return useQuery<FairUseSummary>({
    queryKey: queryKeys.adminFairUseSummary(month ?? "current"),
    queryFn: () => getFairUseSummary({ month }),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useFairUseConfigQuery(enabled = true) {
  return useQuery<FairUseConfigResponse>({
    queryKey: FAIR_USE_CONFIG_QUERY_KEY,
    queryFn: () => getFairUseConfig(),
    enabled,
    // Always refetch on mount: the {config, hash} pair drives optimistic
    // concurrency — a stale cached hash would only surface as a 409 at save
    // time (same rationale as useRawEntityQuery).
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useApproveFairUseRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { requestId: string; blocks: number; month?: string }) =>
      approveFairUseRequest(args),
    onSuccess: () => {
      invalidateFairUseOverviewAndSummary(queryClient)
    },
  })
}

export function useDenyFairUseRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { requestId: string; reason?: string; month?: string }) =>
      denyFairUseRequest(args),
    onSuccess: () => {
      invalidateFairUseOverviewAndSummary(queryClient)
    },
  })
}

export function useCreateFairUseGrantMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { user_id: string; blocks: number; reason?: string; month?: string }) =>
      createFairUseGrant(args),
    onSuccess: () => {
      invalidateFairUseOverviewAndSummary(queryClient)
    },
  })
}

/**
 * Revoke a top-up (platform admins only).
 *
 * Invalidates both the overview and the summary, not just the overview: a
 * revoke moves the user's percentage AND the month's spend, so the KPI row
 * and the "Showing N of M" footer would otherwise both keep quoting the
 * pre-revoke total.
 */
export function useRevokeFairUseGrantMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { grantId: string; reason?: string; month?: string }) =>
      revokeFairUseGrant(args),
    onSuccess: () => {
      invalidateFairUseOverviewAndSummary(queryClient)
    },
  })
}

/** Close a billing month. Invalidates the summary so the lock pill cannot stay "open". */
export function useLockBillingMonthMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { month: string }) => lockBillingMonth(args.month),
    onSuccess: () => {
      invalidateFairUseOverviewAndSummary(queryClient)
    },
  })
}

/**
 * Put one user on one plan.
 *
 * Also invalidates the CONFIG query, which looks unrelated and is not: the
 * override is stored in the fair-use config document (`overrides` on
 * `FairUseConfig.to_dict()`), and that document's value hash is what every
 * config Save sends as `expected_hash`. Since round 4 the plan select and the
 * config tabs live in one area under one provider, which reads the hash once
 * on mount — so without this, assigning a plan and then saving a threshold
 * answers 409 and offers to discard the admin's edits. The response carries
 * no config of its own to publish, so this is a re-read rather than a
 * `setQueryData`.
 */
export function useSetFairUseUserTierMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { userId: string; tier: FairUseTier; reason?: string }) =>
      setFairUseUserTier(args),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.setQueriesData<{ pages: FairUsePeopleResponse[]; pageParams: unknown[] }>(
          { queryKey: FAIR_USE_PEOPLE_QUERY_KEY },
          (cached) =>
            cached
              ? {
                  ...cached,
                  pages: cached.pages.map((page) => ({
                    ...page,
                    people: page.people.map((row) =>
                      row.user === result.user_id ? { ...row, tier: result.tier } : row
                    ),
                  })),
                }
              : cached
        )
      }
      invalidateFairUseOverviewAndSummary(queryClient)
      void queryClient.invalidateQueries({ queryKey: FAIR_USE_CONFIG_QUERY_KEY })
    },
  })
}

/**
 * Save the config, then publish the answer the write itself returned.
 *
 * The PUT responds with the post-write `{config, hash}` from the worker that
 * performed the write, so that — not a fresh GET — is the authority on what was
 * saved. Invalidating the config query instead would re-read
 * `/api/admin/fair-use/config`, which serves the config the SERVING worker
 * holds in memory (`admin.config`): a PUT hot-reloads only the worker that
 * handled it, and every other instance catches up at most once per gate config
 * TTL (`utils/app_setup.py::build_fair_use_config_loader`). A re-read issued
 * immediately after a save can therefore land on an instance that still holds
 * the pre-save config and quietly revert the panel to the old values under a
 * "saved" toast — and hand it a hash that 409s on the next save.
 *
 * Caps, percentages and the month header are computed from the config, so the
 * overview AND the summary are invalidated. They are per-user aggregates the
 * PUT body cannot stand in for.
 *
 * `downgrade_model_options` is GET-only (derived, not in the document). The
 * post-write cache therefore keeps the previous list rather than dropping it
 * with the PUT body — and does not re-read `/config`, which would still be
 * the serving worker's in-memory copy.
 */
export function useUpdateFairUseConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { updates: FairUseConfigUpdate; expectedHash: string }) =>
      updateFairUseConfig(args),
    onSuccess: (result) => {
      const { config, hash } = result
      if (config && hash) {
        queryClient.setQueryData<FairUseConfigResponse>(FAIR_USE_CONFIG_QUERY_KEY, (previous) =>
          mergeFairUseConfigCacheAfterSave(previous, { config, hash })
        )
      }
      invalidateFairUseOverviewAndSummary(queryClient)
    },
  })
}

/**
 * Rebuild the config query cache after a PUT.
 *
 * The write answers `{config, hash}` only. `downgrade_model_options` is
 * derived from `CONFIG_CHAT_MODELS` on GET and is not part of the document,
 * so replacing the cache with the PUT body would drop the list: the fallback
 * select would go empty and a working stored model would be flagged
 * "unknown, not deployed". The GET list is merged back on. When the stored
 * id is unchanged, GET's `selected` is kept (suffix-tolerant resolution).
 * When it changed, `selected` follows the new stored id.
 */
export function mergeFairUseConfigCacheAfterSave(
  previous: FairUseConfigResponse | undefined,
  saved: { config: FairUseConfig; hash: string }
): FairUseConfigResponse {
  // The PUT answers with `{success, config, hash}` only; every server-derived sibling the GET
  // carried (downgrade deployments, the entitlement schema) is kept from the previous cache entry.
  const siblings = { ...(previous ?? {}) }
  const previousOptions = previous?.downgrade_model_options
  if (!Array.isArray(previousOptions)) {
    return { ...siblings, config: saved.config, hash: saved.hash }
  }

  const previousId = previous?.config.downgrade_model
  const nextId = saved.config.downgrade_model
  if (previousId === nextId) {
    return {
      ...siblings,
      config: saved.config,
      hash: saved.hash,
      downgrade_model_options: previousOptions,
    }
  }

  return {
    ...siblings,
    config: saved.config,
    hash: saved.hash,
    downgrade_model_options: previousOptions.map((option) => {
      if (option.id === nextId) {
        return option.selected === true ? option : { ...option, selected: true }
      }
      if (option.selected !== true) return option
      const rest = { ...option }
      delete rest.selected
      return rest
    }),
  }
}
